import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sanitizeInput, getMinPassengersError, validateInput } from '../utils/validation';
import { getTomorrowDate } from '../utils/date';
import { UI_CONFIG, FORM_CONFIG } from '../constants/config';
import { sendContactEnquiry } from '../services/contactService';
import { trackFormSubmission } from '../utils/analytics';
import '../styles/utilities.css';

const ContactForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [cooldown, setCooldown] = useState(0);
  const [messageLength, setMessageLength] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    passengers: '',
    message: ''
  });

  useEffect(() => {
    if (location.state?.selectedTour) {
      const minimumPassengers = location.state?.hasMinimumBooking ? location.state.minimumPeople : '';
      setFormData((prev) => ({
        ...prev,
        passengers: minimumPassengers.toString()
      }));
    }
  }, [location.state]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => {
        setCooldown(cooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const getMinimumPeople = () => {
    return location.state?.minimumPeople || 1;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value
    });

    // Track message length for character counter
    if (name === 'message') {
      setMessageLength(value.length);
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      const newErrors = { ...fieldErrors };
      delete newErrors[name];
      setFieldErrors(newErrors);
    }

    // Clear general validation error when user starts correcting
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sanitize all fields before validation
    const sanitizedData = {
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email),
      phone: sanitizeInput(formData.phone),
      date: formData.date,
      passengers: formData.passengers,
      message: sanitizeInput(formData.message)
    };

    // Validate all fields before submission
    const minimumPeople = getMinimumPeople();
    const errors = {};

    if (!validateInput('name', sanitizedData.name, minimumPeople)) {
      errors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    if (!validateInput('email', sanitizedData.email, minimumPeople)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!validateInput('phone', sanitizedData.phone, minimumPeople)) {
      errors.phone = 'Please enter a valid phone number';
    }
    if (!validateInput('date', sanitizedData.date, minimumPeople)) {
      errors.date = 'Please select a date from tomorrow onwards';
    }
    if (!validateInput('passengers', sanitizedData.passengers, minimumPeople)) {
      errors.passengers = getMinPassengersError(minimumPeople);
    }
    if (!validateInput('message', sanitizedData.message, minimumPeople)) {
      errors.message = 'Message must be at least 5 characters and not exceed 1000 characters';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    trackFormSubmission('contact-form');

    try {
      const result = await sendContactEnquiry(sanitizedData);

      if (result.success) {
        navigate('/success', {
          state: {
            bookingDetails: {
              name: formData.name,
              email: formData.email,
              date: formData.date,
              passengers: formData.passengers
            }
          }
        });
      } else {
        navigate('/error', {
          state: {
            errorMessage: result.message || 'Failed to send your enquiry. Please try again or contact us directly.'
          }
        });
      }
    } catch (error) {
      navigate('/error', {
        state: {
          errorMessage: 'Network error. Please check your connection and try again.'
        }
      });
    } finally {
      setLoading(false);
      setCooldown(FORM_CONFIG.submitCooldown);
    }
  };

  const minimumPeople = getMinimumPeople();

  return (
    <div className="contact-form">
      <h2>Book Your Tour</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            maxLength={UI_CONFIG.nameMaxLength}
            aria-invalid={!!fieldErrors.name}
            required
          />
          {fieldErrors.name && <div className="error-message">{fieldErrors.name}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onInput={(e) => {
              if (e.target.value.includes(' ')) {
                e.target.value = e.target.value.replace(/ /g, '');
              }
            }}
            maxLength={UI_CONFIG.emailMaxLength}
            aria-invalid={!!fieldErrors.email}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            required
          />
          {fieldErrors.email && <div id="email-error" className="error-message">{fieldErrors.email}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number (optional)</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onInput={(e) => {
              // Remove any letters, keep only digits and phone characters
              e.target.value = e.target.value.replace(/[a-zA-Z]/g, '');
            }}
            maxLength={UI_CONFIG.phoneMaxLength}
            placeholder="+44 7911 123456"
            inputMode="tel"
            autoComplete="tel"
            aria-invalid={!!fieldErrors.phone}
            aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
          />
          {fieldErrors.phone && <div id="phone-error" className="error-message">{fieldErrors.phone}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Preferred Date</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              min={getTomorrowDate()}
              aria-invalid={!!fieldErrors.date}
            />
            {fieldErrors.date && <div className="error-message">{fieldErrors.date}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="passengers">
              Number of Passengers
              {minimumPeople && (
                <span className="minimum-requirement"> (Minimum: {minimumPeople})</span>
              )}
            </label>
            <input
              type="number"
              id="passengers"
              name="passengers"
              value={formData.passengers}
              onChange={handleChange}
              min={minimumPeople ? minimumPeople : '1'}
              max={UI_CONFIG.maxPassengers}
              title={
                minimumPeople
                  ? `This tour requires a minimum of ${minimumPeople} passengers`
                  : `Enter number of passengers (1-${UI_CONFIG.maxPassengers})`
              }
              aria-invalid={!!fieldErrors.passengers}
            />
            {fieldErrors.passengers && <div className="error-message">{fieldErrors.passengers}</div>}
            {validationError && <div className="error-message">{validationError}</div>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message">
            Additional Information *
            <span className="character-count">
              {messageLength} / {UI_CONFIG.messageMaxLength}
            </span>
          </label>
          <textarea
            id="message"
            name="message"
            rows="4"
            value={formData.message}
            onChange={handleChange}
            maxLength={UI_CONFIG.messageMaxLength}
            placeholder="Tell us about any special requirements or questions..."
            aria-invalid={!!fieldErrors.message}
            aria-describedby={fieldErrors.message ? 'message-error' : 'message-hint'}
            required
          ></textarea>
          <div id="message-hint" className="form-hint">Minimum 5 characters, maximum {UI_CONFIG.messageMaxLength} characters</div>
          {fieldErrors.message && <div id="message-error" className="error-message">{fieldErrors.message}</div>}
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading || cooldown > 0}>
          {loading ? (
            <span>
              <span className="loading-spinner"></span>
              Sending...
            </span>
          ) : cooldown > 0 ? (
            `Please wait ${cooldown}s before submitting again`
          ) : (
            'Send Enquiry'
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
